// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { Input } from '@imprivata-cloud/design-system';
import { Clock } from 'lucide-react';
import { type KeyboardEvent, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { keyFilter } from './keyFilter';
import {
  acceptsSexagesimal,
  acceptsTotal,
  fromHoursMinutesSeconds,
  type LimitError,
  limitErrorOf,
  MAX_DURATION_HOURS,
  toHoursMinutesSeconds,
} from './workstationSettingsModel';

const KEYS = 'portal.eamc.security.workstation.desktop';
const SECONDS_PER_MINUTE = 60;

const range = (length: number) => Array.from({ length }, (_, i) => i);

export type DurationLimitProps = { bound: 'max' | 'min'; seconds: number; label: string };

type DurationFieldProps = {
  label: string;
  // In seconds.
  value: number;
  // The allowed range, in seconds, such as the BFF's `customRange`.
  range?: { min: number; max: number };
  // Listed values, in seconds, allowed even outside `range`.
  presets: readonly number[];
  // Another duration's saved value, which this one must not pass; `label` names it in the error.
  limit?: DurationLimitProps;
  describedBy?: string;
  disabled: boolean;
  onCommit: (value: number, revert: () => void) => void;
};

type DurationError =
  | 'format-error'
  | 'hours-error'
  | 'minutes-error'
  | 'seconds-error'
  | 'duration-range-error'
  | LimitError;

const PARTS = ['hours', 'minutes', 'seconds'] as const;
type Part = (typeof PARTS)[number];
type Parts = Record<Part, number>;

const PART_OPTIONS: Record<Part, readonly number[]> = {
  hours: range(MAX_DURATION_HOURS + 1),
  minutes: range(SECONDS_PER_MINUTE),
  seconds: range(SECONDS_PER_MINUTE),
};

const PART_LABEL_KEYS: Record<Part, string> = {
  hours: 'hours-label',
  minutes: 'unit-label',
  seconds: 'seconds-label',
};

const DURATION_TEXT = /^(\d+):(\d{1,2}):(\d{1,2})$/;

const pad = (value: number) => String(value).padStart(2, '0');

const formatParts = (parts: Parts) => PARTS.map(part => pad(parts[part])).join(':');

export const formatDuration = (seconds: number) => formatParts(toHoursMinutesSeconds(seconds));

const parseDuration = (text: string): Parts | null => {
  const match = DURATION_TEXT.exec(text.trim());
  return match && { hours: Number(match[1]), minutes: Number(match[2]), seconds: Number(match[3]) };
};

const totalOf = ({ hours, minutes, seconds }: Parts) =>
  fromHoursMinutesSeconds(hours, minutes, seconds);

const draftOf = (value: number) => ({
  base: value,
  text: formatDuration(value),
  error: null as DurationError | null,
});

const durationErrorOf = (
  parts: Parts,
  { range, presets, limit }: Pick<DurationFieldProps, 'range' | 'presets' | 'limit'>,
): DurationError | null => {
  if (!acceptsSexagesimal(parts.minutes)) {
    return 'minutes-error';
  }
  if (!acceptsSexagesimal(parts.seconds)) {
    return 'seconds-error';
  }
  const total = totalOf(parts);
  if (!acceptsTotal(total, range, presets)) {
    return range ? 'duration-range-error' : 'hours-error';
  }
  return limitErrorOf(limit, total);
};

// A range end in words, for example "1 hour 30 minutes"; zero parts are left out.
const durationText = (t: (key: string, options: { count: number }) => string, total: number) => {
  const parts = toHoursMinutesSeconds(total);
  const shown = PARTS.filter(part => parts[part] > 0);
  return (shown.length ? shown : (['seconds'] as const))
    .map(part => t(`${KEYS}.range-${part}`, { count: parts[part] }))
    .join(' ');
};

const handleDurationKeyDown = keyFilter(/[0-9:]/);

// One `hh:mm:ss` box that takes typing, with a dropdown of hours, minutes and seconds columns
// (user, 2026-10-19). Saves once focus leaves the box and its dropdown, or on Enter, so picking
// the hours and then the minutes does not save half an edit.
const DurationField = ({
  label,
  value,
  range,
  presets,
  limit,
  describedBy,
  disabled,
  onCommit,
}: DurationFieldProps) => {
  const { t } = useTranslation();
  const errorId = useId();
  const popupId = useId();
  const optionId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(() => draftOf(value));
  // Stored when the saved value changes, as in NumberField.
  let current = draft;
  if (draft.base !== value) {
    current = draftOf(value);
    setDraft(current);
  }
  const selected = parseDuration(current.text) ?? toHoursMinutesSeconds(value);

  const commit = () => {
    setOpen(false);
    const parts = parseDuration(current.text);
    if (!parts) {
      setDraft({ ...current, error: 'format-error' });
      return;
    }
    const next = totalOf(parts);
    // A saved value outside the range is only flagged once it is edited.
    if (next === value) {
      setDraft(draftOf(value));
      return;
    }
    const error = durationErrorOf(parts, { range, presets, limit });
    if (error) {
      setDraft({ ...current, error });
      return;
    }
    setDraft({ ...draftOf(next), base: value });
    onCommit(next, () => setDraft(draftOf(value)));
  };

  const pick = (part: Part, option: number) =>
    setDraft({ ...current, text: formatParts({ ...selected, [part]: option }), error: null });

  const close = () => {
    setOpen(false);
    inputRef.current?.focus();
  };

  const onColumnKeyDown = (part: Part, event: KeyboardEvent<HTMLDivElement>) => {
    const options = PART_OPTIONS[part];
    const index = options.indexOf(selected[part]);
    const step: Partial<Record<string, number>> = {
      ArrowDown: Math.min(index + 1, options.length - 1),
      ArrowUp: Math.max(index - 1, 0),
      Home: 0,
      End: options.length - 1,
    };
    const target = step[event.key];
    if (target !== undefined) {
      event.preventDefault();
      pick(part, options[target]);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      commit();
      inputRef.current?.focus();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      close();
    }
  };

  const errorText = (error: DurationError) => {
    if (error === 'duration-range-error' && range) {
      return t(`${KEYS}.${error}`, {
        min: durationText(t, range.min),
        max: durationText(t, range.max),
      });
    }
    if ((error === 'longer-than-error' || error === 'shorter-than-error') && limit) {
      return t(`${KEYS}.${error}`, { setting: limit.label, value: formatDuration(limit.seconds) });
    }
    return t(`${KEYS}.${error}`);
  };

  return (
    <fieldset
      className="workstation-number workstation-duration"
      onBlur={event => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          commit();
        }
      }}
    >
      <Input
        ref={inputRef}
        className="workstation-input"
        label={label}
        role="combobox"
        aria-expanded={open}
        aria-controls={open ? popupId : undefined}
        aria-haspopup="dialog"
        // Replaces the DS Input's own description, so the error and the caller's status can both be listed.
        aria-describedby={
          [current.error ? errorId : undefined, describedBy].filter(Boolean).join(' ') || undefined
        }
        value={current.text}
        disabled={disabled}
        inputMode="numeric"
        error={current.error !== null}
        trailing={
          !disabled && (
            <Clock
              aria-hidden
              size={16}
              className="workstation-duration-toggle"
              onMouseDown={event => {
                event.preventDefault();
                inputRef.current?.focus();
                setOpen(!open);
              }}
            />
          )
        }
        onClick={() => setOpen(true)}
        onKeyDown={event => {
          handleDurationKeyDown(event);
          if (event.key === 'Enter') {
            commit();
          } else if (event.key === 'Escape') {
            setOpen(false);
          } else if (event.key === 'ArrowDown') {
            event.preventDefault();
            setOpen(true);
          }
        }}
        onChange={event => setDraft({ ...current, text: event.target.value })}
      />
      {open && !disabled && (
        <div
          id={popupId}
          role="dialog"
          aria-label={label}
          className="workstation-duration-popup"
          // Keeps focus in the box, so a click on an option does not end the edit.
          onMouseDown={event => event.preventDefault()}
        >
          {PARTS.map(part => (
            <div
              key={part}
              role="listbox"
              tabIndex={0}
              aria-label={t(`${KEYS}.${PART_LABEL_KEYS[part]}`, { setting: label })}
              aria-activedescendant={`${optionId}-${part}-${selected[part]}`}
              className="workstation-duration-column"
              onKeyDown={event => onColumnKeyDown(part, event)}
            >
              {PART_OPTIONS[part].map(option => (
                // biome-ignore lint/a11y/useKeyWithClickEvents: the listbox takes the keys, as aria-activedescendant expects
                <div
                  key={option}
                  id={`${optionId}-${part}-${option}`}
                  role="option"
                  tabIndex={-1}
                  aria-selected={selected[part] === option}
                  ref={
                    selected[part] === option
                      ? node => {
                          // Scrolls the column only; scrollIntoView would move the page too.
                          const column = node?.parentElement;
                          if (node && column) {
                            column.scrollTop =
                              node.offsetTop - (column.clientHeight - node.offsetHeight) / 2;
                          }
                        }
                      : undefined
                  }
                  onClick={() => pick(part, option)}
                >
                  {pad(option)}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
      {current.error && (
        <p id={errorId} className="workstation-error">
          {errorText(current.error)}
        </p>
      )}
    </fieldset>
  );
};

export default DurationField;
